import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils/cn';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { NotificationsFeed } from '@/components/common/NotificationsFeed';
import { MobileNavTrigger } from '../Sidebar/Sidebar';

interface TopBarProps {
  userEmail: string;
  userId: string;
  tenantId: string | null;
  onOpenMobileNav: () => void;
}

// Slim top bar (h-12) with a mobile-nav trigger, a flexible centre, and the
// notifications + user menu on the right. Designed to coexist with the Sidebar
// — the Sidebar owns the brand mark, this owns user-context controls.
export function TopBar({
  userEmail,
  userId,
  tenantId,
  onOpenMobileNav,
}: TopBarProps) {
  return (
    <header className="sticky top-0 z-30 flex items-center justify-between h-12 bg-white border-b border-gray-200 px-4">
      <div className="flex items-center gap-2">
        <MobileNavTrigger onOpen={onOpenMobileNav} />
      </div>
      <div className="flex items-center gap-2">
        <NotificationsFeed userId={userId} tenantId={tenantId} />
        <UserMenu userEmail={userEmail} />
      </div>
    </header>
  );
}

function UserMenu({ userEmail }: { userEmail: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="hidden sm:inline text-small text-gray-700 max-w-[180px] truncate">
          {userEmail}
        </span>
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary text-secondary text-tiny font-semibold">
          {userEmail.charAt(0).toUpperCase()}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 mt-1 w-48 bg-white rounded-card shadow-card border border-gray-200 py-1',
          )}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
            className="block w-full text-left px-3 py-2 text-small text-gray-700 hover:bg-gray-50"
            role="menuitem"
          >
            Profile
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/profile/security');
            }}
            className="block w-full text-left px-3 py-2 text-small text-gray-700 hover:bg-gray-50"
            role="menuitem"
          >
            Security
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full text-left px-3 py-2 text-small text-danger hover:bg-gray-50"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
